import React, { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, Loader2, X, AlertCircle } from 'lucide-react';

interface FloatingChatInputProps {
  onSubmit: (prompt: string) => void;
  loading?: boolean;
  disabled?: boolean;
  error?: string | null;
  onDismissError?: () => void;
}

export function FloatingChatInput({ onSubmit, loading, disabled, error, onDismissError }: FloatingChatInputProps) {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = '0px';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  };

  useEffect(() => {
    resizeTextarea();
  }, [input]);

  const submitPrompt = () => {
    if (!input.trim() || loading || disabled) return;
    onSubmit(input.trim());
    setInput('');
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    submitPrompt();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitPrompt();
    }
  };

  return (
    <div className="absolute bottom-4 left-4 right-4 z-10 flex flex-col gap-2">
      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl shadow-lg text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          {onDismissError && (
            <button
              type="button"
              aria-label="Dismiss floating chat error"
              onClick={onDismissError}
              className="p-1 hover:bg-red-100 rounded transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        className={`
          flex items-center gap-2 px-3 py-2
          bg-white border border-gray-200 rounded-xl
          shadow-lg transition-all duration-200
          ${focused ? 'ring-2 ring-blue-500 border-transparent' : 'hover:border-gray-300'}
        `}
      >
        <Sparkles className="h-4 w-4 text-blue-500 flex-shrink-0" />
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder={loading ? "Waiting for a response..." : "Ask AI to help with your LaTeX..."}
          disabled={loading || disabled}
          className="max-h-40 min-h-[40px] flex-1 resize-none bg-transparent border-none py-1 text-sm outline-none placeholder-gray-400 disabled:text-gray-400"
        />
        <button
          type="submit"
          aria-label="Send floating chat prompt"
          disabled={!input.trim() || loading || disabled}
          className="p-1.5 rounded-lg text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </form>
    </div>
  );
}
